/**
 * The settings surface — theme, locale, and timezone, each auto-detected by
 * default and user-overridable. Pure presentation over `@4f/ui` primitives + the
 * injected {@link SettingsStore} (DIP): the controls edit the OVERRIDE (the
 * `auto` choice clears it back to detection) while the store owns persistence +
 * the effective values the shell's `ThemeProvider` / `I18nProvider` / timezone
 * formatter read. Every user-facing string is a `settings.*` i18n key.
 */

import { HAS_LOCALE_CHOICE, SUPPORTED_LOCALES } from '@4f/i18n';
import type { Theme } from '@4f/shared';
import { Radio, type RadioOption, Select, type SelectOption, useT } from '@4f/ui';
import { createMemo, type JSX, Show } from 'solid-js';
import type { DataRightsStore } from '../../stores/data-rights.ts';
import type { SettingsStore } from '../../stores/settings.ts';
import { DataRightsPanel } from './DataRightsPanel.tsx';
import {
  FOLLOW_AUTO,
  localeFromChoice,
  themeFromChoice,
  timeZoneChoices,
  timeZoneFromChoice,
  timeZoneLabel,
} from './settings-model.ts';

export interface SettingsSurfaceProps {
  readonly store: SettingsStore;
  /**
   * The self-serve export/delete controls. REQUIRED, not optional: the marketing
   * site tells users both live in their settings, and an optional panel is how
   * that stayed untrue.
   */
  readonly dataRights: DataRightsStore;
  /** Renders an ISO instant in the user's own timezone (the shell's formatter). */
  formatDate(iso: string): string;
  /** Ends the session — offered after a deletion request, on the user's click. */
  onSignOut(): void;
}

/** The theme choices, in display order — `auto` follows the OS. */
const THEME_VALUES: readonly Theme[] = ['auto', 'light', 'dark'];

export function SettingsSurface(props: SettingsSurfaceProps): JSX.Element {
  const { t } = useT();

  const themeOptions = createMemo<RadioOption[]>(() =>
    THEME_VALUES.map((value) => ({ value, label: t(`settings.theme.options.${value}`) })),
  );

  const localeOptions = createMemo<SelectOption[]>(() => [
    { value: FOLLOW_AUTO, label: t('settings.autoDetect') },
    ...SUPPORTED_LOCALES.map((value) => ({ value, label: t(`settings.locale.options.${value}`) })),
  ]);

  const timeZoneOptions = createMemo<SelectOption[]>(() => [
    {
      value: FOLLOW_AUTO,
      label: t('settings.timezone.auto', { zone: timeZoneLabel(props.store.detectedTimeZone) }),
    },
    ...timeZoneChoices(props.store.detectedTimeZone).map((zone) => ({
      value: zone,
      label: timeZoneLabel(zone),
    })),
  ]);

  return (
    <div class="f4-settings">
      <Radio
        name="f4-settings-theme"
        legend={t('settings.theme.label')}
        value={props.store.theme()}
        options={themeOptions()}
        onChange={(value) => void props.store.setTheme(themeFromChoice(value))}
      />

      {/* Only offer a language control when there is an actual choice to make —
          a dropdown with one option is a dead control that implies otherwise. */}
      <Show when={HAS_LOCALE_CHOICE}>
        <Select
          label={t('settings.locale.label')}
          value={props.store.localeOverride() ?? FOLLOW_AUTO}
          options={localeOptions()}
          onValue={(value) => void props.store.setLocale(localeFromChoice(value))}
        />
      </Show>

      <Select
        label={t('settings.timezone.label')}
        value={props.store.timeZoneOverride() ?? FOLLOW_AUTO}
        options={timeZoneOptions()}
        onValue={(value) => void props.store.setTimeZone(timeZoneFromChoice(value))}
      />

      <Show when={props.store.saving()}>
        <p class="f4-genui__hint" role="status">
          {t('settings.saving')}
        </p>
      </Show>
      <Show when={props.store.error()}>
        {(error) => (
          <p class="f4-genui__error" role="alert">
            {error().message}
          </p>
        )}
      </Show>

      <DataRightsPanel
        store={props.dataRights}
        formatDate={props.formatDate}
        onSignOut={props.onSignOut}
      />
    </div>
  );
}
